import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as DuplicateIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import EnvironmentIcon from '../components/icons/EnvironmentIcon';
import { apiService, Environment, type EnvironmentVariable } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import { EnvironmentVariablesPage } from '../components/EnvironmentVariablesPage';
import { getHelpPathForPage } from '../utils/helpPageMapping';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { validateEnvironmentBaseUrl as validateEnvironmentUrl, normalizeEnvironmentUrlForComparison } from '../utils/environmentBaseUrlValidation';
import {
  ENVIRONMENTS_CHANGED_EVENT,
  type EnvironmentsChangedDetail,
} from '../utils/environmentVariablesEvents';
import { PROJECT_SELECT_CONTROL_WIDTH } from './Projects';
import { countManageableProjectScopedVariables } from '../utils/manageableProjectVariables';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';

/** Match Collections / Dashboard chrome: canvas + inner 14px cards. */
const ENVIRONMENTS_CARD_RADIUS = '14px';
const environmentsPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiCard-root': {
    borderRadius: `${ENVIRONMENTS_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-subtitle2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'text.disabled',
    opacity: 1,
  },
  '& .MuiInputBase-input::-webkit-input-placeholder': {
    color: 'text.disabled',
    opacity: 1,
  },
  '& textarea.MuiInputBase-input::placeholder': {
    color: 'text.disabled',
    opacity: 1,
  },
} as const;
const ENVIRONMENTS_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  overflow: 'hidden',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
} as const;
const environmentsCardSx = {
  mb: 1.5,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative' as const,
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${ENVIRONMENTS_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;

const STORAGE_KEY_SELECTED_ENV_ID_PREFIX = 'environmentsSelectedId_';
const getStorageKeyForProject = (projectId: number) => `${STORAGE_KEY_SELECTED_ENV_ID_PREFIX}${projectId}`;
/** Narrower on 1366 / 14" Mac; full Collections width from xl. */
const LEFT_PANEL_WIDTH = { xs: '100%', md: 220, lg: 248, xl: 280 } as const;
const DEFAULT_SIDEBAR_ROWS_PER_PAGE = 10;
const DEFAULT_MAX_ENVIRONMENTS = 100;
const ENVIRONMENT_NAME_MAX_LENGTH = 30;

/** Strip leading spaces and collapse runs of spaces while the user is typing. */
function sanitizeEnvironmentNameWhileTyping(name: string): string {
  return name.replace(/^\s+/, '').replace(/ {2,}/g, ' ').slice(0, ENVIRONMENT_NAME_MAX_LENGTH);
}

/** Canonical name persisted on save (trim ends, single spaces between words). */
function normalizeEnvironmentNameForSave(name: string): string {
  return name.trim().replace(/\s+/g, ' ').slice(0, ENVIRONMENT_NAME_MAX_LENGTH);
}
const ENV_LIMIT_REACHED_MESSAGE = 'Environment limit reached. Upgrade plan or delete existing.';

function normalizeEnvName(name: string): string {
  return normalizeEnvironmentNameForSave(name).toLowerCase();
}

function formatEnvironmentRequestUsage(count: number | undefined): string {
  const n = count ?? 0;
  return n === 1 ? '1 request' : `${n} requests`;
}

function buildLastEnvironmentProjectVarsBlockMessage(envName: string, projectScopedCount: number): string {
  const noun = projectScopedCount === 1 ? 'variable' : 'variables';
  return (
    `Cannot delete "${envName}" because it is the only environment in this project. ` +
    `Delete all project-scoped ${noun} first (${projectScopedCount} remaining), then try again.`
  );
}

function environmentListSecondary(env: Environment): string {
  const usage = formatEnvironmentRequestUsage(env.requestCount);
  const url = env.baseUrl ?? env.url ?? '';
  return url ? `${usage} · ${url}` : usage;
}

function hasDuplicateEnvironmentUrl(
  url: string,
  environmentList: Environment[],
  excludeEnvironmentId?: number
): boolean {
  const normalized = normalizeEnvironmentUrlForComparison(url);
  if (!normalized) return false;
  return environmentList.some((env) => {
    if (excludeEnvironmentId != null && env.id === excludeEnvironmentId) return false;
    return normalizeEnvironmentUrlForComparison(env.baseUrl ?? env.url) === normalized;
  });
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const backendMessage =
    (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message ||
    (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.error ||
    (err as { message?: string })?.message;
  return typeof backendMessage === 'string' && backendMessage.trim() ? backendMessage : fallback;
}

function fitNameWithinMaxLength(
  prefix: string,
  core: string,
  suffix: string,
  maxLength: number
): string {
  const reserved = prefix.length + suffix.length;
  const maxCoreLength = Math.max(1, maxLength - reserved);
  const trimmedCore = core.trim().slice(0, maxCoreLength).trimEnd();
  return `${prefix}${trimmedCore}${suffix}`;
}

/**
 * Picks a duplicate environment name that does not collide with existing names in the project
 * (case-insensitive), same rule as manual create: "Copy of …", then "Copy of … (2)", "(3)", …
 * Truncates the source name when needed so the result fits ENVIRONMENT_NAME_MAX_LENGTH.
 */
function getUniqueDuplicateEnvironmentName(sourceName: string, existing: Environment[]): string {
  const taken = new Set(existing.map((e) => normalizeEnvName(e.name)));
  const basePrefix = 'Copy of ';
  const source = sourceName.trim();

  const firstCandidate = fitNameWithinMaxLength(
    basePrefix,
    source,
    '',
    ENVIRONMENT_NAME_MAX_LENGTH
  );
  if (!taken.has(normalizeEnvName(firstCandidate))) {
    return firstCandidate;
  }

  let n = 2;
  while (n < 10_000) {
    const suffix = ` (${n})`;
    const candidate = fitNameWithinMaxLength(
      basePrefix,
      source,
      suffix,
      ENVIRONMENT_NAME_MAX_LENGTH
    );
    if (!taken.has(normalizeEnvName(candidate))) {
      return candidate;
    }
    n += 1;
  }

  return fitNameWithinMaxLength('Copy ', source, ` ${Date.now() % 100_000}`, ENVIRONMENT_NAME_MAX_LENGTH);
}

interface CreateEnvironmentData {
  name: string;
  description: string;
  url: string;
}

/** Viewport anchorPosition for MUI Select (stable under CSS zoom; opens above/below the field). */
function useZoomSafeSelectMenu(extra?: {
  paperSx?: Record<string, unknown>;
  menuListSx?: Record<string, unknown>;
}) {
  const [menuState, setMenuState] = useState({
    open: false,
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
  });
  const anchorElRef = useRef<HTMLElement | null>(null);

  const measureFromEl = (combobox: HTMLElement) => {
    const rect = combobox.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = typeof window !== 'undefined' ? window.innerHeight - rect.bottom : 320;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < 120 && spaceAbove > spaceBelow;
    return {
      top: Math.round(openUpward ? rect.top - gap : rect.bottom + gap),
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      openUpward,
    };
  };

  const onOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement;
    const combobox =
      (el.matches('[role="combobox"]')
        ? el
        : el.querySelector<HTMLElement>('[role="combobox"]')) ?? el;
    anchorElRef.current = combobox;
    const measured = measureFromEl(combobox);
    flushSync(() => {
      setMenuState({
        open: true,
        ...measured,
      });
    });
  };

  const onClose = () => setMenuState((prev) => ({ ...prev, open: false }));

  const remeasureWhileOpen = () => {
    const combobox = anchorElRef.current;
    if (!combobox) return;
    const measured = measureFromEl(combobox);
    setMenuState((prev) =>
      prev.open
        ? {
            ...prev,
            ...measured,
          }
        : prev
    );
  };

  const MenuProps = {
    disableScrollLock: true,
    anchorReference: 'anchorPosition' as const,
    anchorPosition: { top: menuState.top, left: menuState.left },
    transformOrigin: {
      vertical: (menuState.openUpward ? 'bottom' : 'top') as 'bottom' | 'top',
      horizontal: 'left' as const,
    },
    sx: { zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal },
    TransitionProps: {
      onEntering: remeasureWhileOpen,
      onEntered: remeasureWhileOpen,
    },
    PaperProps: {
      sx: {
        ...(menuState.openUpward ? { mb: 0.5, mt: 0 } : { mt: 0.5 }),
        zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal,
        ...(menuState.width > 0
          ? {
              width: menuState.width,
              minWidth: menuState.width,
              maxWidth: 'min(100vw - 16px, 560px)',
            }
          : {}),
        ...extra?.paperSx,
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 320,
        overflowY: 'auto' as const,
        py: 0.5,
        ...extra?.menuListSx,
      },
    },
  };

  return { menuOpen: menuState.open, onOpen, onClose, MenuProps };
}

interface EnvironmentsSidebarPaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

function EnvironmentsSidebarPagination({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: EnvironmentsSidebarPaginationProps) {
  const zoomMenu = useZoomSafeSelectMenu({
    paperSx: {
      // Combobox is ~32px wide; keep dropdown readable and opaque over the environments list.
      minWidth: 72,
      width: 'auto',
      maxHeight: 200,
      bgcolor: 'background.paper',
      backgroundImage: 'none',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 6,
      borderRadius: 1,
      overflow: 'hidden',
    },
    menuListSx: {
      maxHeight: 200,
      py: 0.25,
      bgcolor: 'background.paper',
      '& .MuiMenuItem-root': { fontSize: '0.8125rem', minHeight: 28, py: 0.5 },
    },
  });

  return (
    <TablePagination
      component="div"
      count={count}
      page={page}
      onPageChange={(_, newPage) => onPageChange(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage=""
      labelDisplayedRows={({ from, to, count: total }) => `${from}-${to}/${total}`}
      SelectProps={{
        open: zoomMenu.menuOpen,
        onOpen: zoomMenu.onOpen,
        onClose: zoomMenu.onClose,
        MenuProps: zoomMenu.MenuProps,
      }}
      sx={{
        flexShrink: 0,
        borderTop: 1,
        borderColor: 'divider',
        px: 0.5,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        '& .MuiTablePagination-toolbar': {
          flexWrap: 'wrap',
          alignItems: 'center',
          minHeight: 28,
          px: 0,
          py: 0.5,
          gap: 0.25,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        },
        '& .MuiTablePagination-spacer': {
          display: 'none',
        },
        '& .MuiTablePagination-selectLabel': {
          display: 'none',
        },
        '& .MuiTablePagination-input': {
          m: 0,
          mr: 0.25,
          flexShrink: 0,
        },
        '& .MuiTablePagination-select': {
          fontSize: '0.8125rem',
          py: 0,
          pr: '28px !important',
          minWidth: 40,
          height: 24,
        },
        '& .MuiTablePagination-displayedRows': {
          fontSize: '0.8125rem',
          m: 0,
          whiteSpace: 'nowrap',
          flexShrink: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.2,
        },
        '& .MuiTablePagination-actions': {
          ml: 'auto',
          flexShrink: 0,
          '& .MuiIconButton-root': {
            p: 0.25,
          },
        },
      }}
    />
  );
}

const Environments: React.FC = () => {
  const navigate = useNavigate();
  const { projects, selectedProject, setSelectedProject, isLoading: projectsLoading } = useProjects();
  const projectSelectRef = useRef<HTMLDivElement | null>(null);
  const [projectMenuWidth, setProjectMenuWidth] = useState(0);
  const projectZoomMenu = useZoomSafeSelectMenu({
    paperSx:
      projectMenuWidth > 0
        ? {
            width: `${projectMenuWidth}px !important`,
            minWidth: `${projectMenuWidth}px !important`,
            maxWidth: `${projectMenuWidth}px !important`,
          }
        : undefined,
    menuListSx: {
      maxHeight: 40 * 8 + 16,
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      '& .MuiMenuItem-root': {
        minHeight: 40,
        whiteSpace: 'nowrap',
      },
    },
  });

  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [baseUrl, setBaseUrl] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateSourceEnvironmentId, setDuplicateSourceEnvironmentId] = useState<number | null>(null);
  const [newEnvironment, setNewEnvironment] = useState<CreateEnvironmentData>({
    name: '',
    description: '',
    url: '',
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState<Environment | null>(null);
  const [nameOnlyEdit, setNameOnlyEdit] = useState(false);
  const [editForm, setEditForm] = useState<CreateEnvironmentData>({
    name: '',
    description: '',
    url: '',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState<Environment | null>(null);
  const [deleteDialogBlockedMessage, setDeleteDialogBlockedMessage] = useState<string | null>(null);

  const [contextMenuAnchor, setContextMenuAnchor] = useState<null | HTMLElement>(null);
  const [contextMenuEnvironment, setContextMenuEnvironment] = useState<Environment | null>(null);
  const [maxEnvironments, setMaxEnvironments] = useState<number>(DEFAULT_MAX_ENVIRONMENTS);
  const [subscriptionLimitsLoaded, setSubscriptionLimitsLoaded] = useState(false);
  const [sidebarPage, setSidebarPage] = useState(0);
  const [sidebarRowsPerPage, setSidebarRowsPerPage] = useState(DEFAULT_SIDEBAR_ROWS_PER_PAGE);
  const environmentCount = environments.length;
  const sidebarMaxPage = Math.max(0, Math.ceil(environments.length / sidebarRowsPerPage) - 1);
  const effectiveSidebarPage = Math.min(sidebarPage, sidebarMaxPage);
  const paginatedEnvironments = useMemo(
    () =>
      environments.slice(
        effectiveSidebarPage * sidebarRowsPerPage,
        effectiveSidebarPage * sidebarRowsPerPage + sidebarRowsPerPage
      ),
    [environments, effectiveSidebarPage, sidebarRowsPerPage]
  );
  const isEnvironmentLimitReached =
    subscriptionLimitsLoaded &&
    Boolean(selectedProject?.id) &&
    environmentCount >= maxEnvironments;

  const refreshSubscriptionLimits = useCallback(() => {
    return apiService
      .getSubscriptionDetails()
      .then((details) => {
        const limit = details?.limits?.maxEnvironments;
        if (typeof limit === 'number' && limit > 0) {
          setMaxEnvironments(limit);
        }
        setSubscriptionLimitsLoaded(true);
      })
      .catch(() => {
        setSubscriptionLimitsLoaded(true);
      });
  }, []);

  useEffect(() => {
    refreshSubscriptionLimits();
  }, [refreshSubscriptionLimits]);

  const fetchEnvironments = useCallback(async (opts?: { preferEnvironmentId?: number }) => {
    if (!selectedProject?.id) {
      setEnvironments([]);
      setSelectedEnvironment(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getEnvironments(selectedProject.id);
      setEnvironments(data);
      const storageKey = getStorageKeyForProject(selectedProject.id);
      setSelectedEnvironment((prev) => {
        if (data.length === 0) return null;
        if (opts?.preferEnvironmentId != null) {
          const preferred = data.find((e) => e.id === opts.preferEnvironmentId);
          if (preferred) return preferred;
        }
        const savedId = localStorage.getItem(storageKey);
        if (savedId) {
          const id = Number(savedId);
          const env = data.find((e) => e.id === id);
          if (env) return env;
        }
        return prev && data.some((e) => e.id === prev.id) ? prev : data[0];
      });
    } catch (err) {
      setError('Failed to fetch environments');
      console.error('Error fetching environments:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    void fetchEnvironments();
  }, [fetchEnvironments]);

  useEffect(() => {
    const onEnvironmentsChanged = (event: Event) => {
      const detail = (event as CustomEvent<EnvironmentsChangedDetail>).detail;
      if (!selectedProject?.id) return;
      if (detail?.projectId != null && Number(detail.projectId) !== Number(selectedProject.id)) {
        return;
      }
      void fetchEnvironments({
        preferEnvironmentId:
          detail?.environmentId != null ? Number(detail.environmentId) : undefined,
      });
      void refreshSubscriptionLimits();
    };
    window.addEventListener(ENVIRONMENTS_CHANGED_EVENT, onEnvironmentsChanged);
    return () => window.removeEventListener(ENVIRONMENTS_CHANGED_EVENT, onEnvironmentsChanged);
  }, [selectedProject?.id, fetchEnvironments, refreshSubscriptionLimits]);

  useEffect(() => {
    setSidebarPage(0);
  }, [selectedProject?.id]);

  useEffect(() => {
    if (selectedEnvironment?.id == null) return;
    const idx = environments.findIndex((e) => e.id === selectedEnvironment.id);
    if (idx < 0) return;
    const pageForRow = Math.floor(idx / sidebarRowsPerPage);
    setSidebarPage((prev) => (prev === pageForRow ? prev : pageForRow));
  }, [selectedEnvironment?.id, environments, sidebarRowsPerPage]);

  useEffect(() => {
    if (selectedProject?.id && selectedEnvironment) {
      localStorage.setItem(getStorageKeyForProject(selectedProject.id), String(selectedEnvironment.id));
    }
  }, [selectedProject?.id, selectedEnvironment]);

  const loadEnvironmentDetails = useCallback(async (env: Environment) => {
    if (!env) return;
    setError(null);
    try {
      const envDetails = await apiService.getEnvironment(env.id);
      const url = envDetails.baseUrl ?? envDetails.url ?? '';
      setBaseUrl(url);
    } catch (err) {
      setError('Failed to load environment details');
      console.error('Error loading environment details:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedEnvironment) {
      loadEnvironmentDetails(selectedEnvironment);
    } else {
      setBaseUrl('');
    }
  }, [selectedEnvironment, loadEnvironmentDetails]);

  const handleSelectEnvironment = (env: Environment) => {
    setSelectedEnvironment(env);
  };

  const handleCreateEnvironment = async () => {
    if (!selectedProject?.id) {
      setError('Select a project first');
      return;
    }
    const sanitizedName = normalizeEnvironmentNameForSave(newEnvironment.name);
    if (!sanitizedName) {
      setError('Environment name is required');
      return;
    }
    if (sanitizedName.length > ENVIRONMENT_NAME_MAX_LENGTH) {
      setError(`Environment name must be ${ENVIRONMENT_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }
    if (isEnvironmentLimitReached) {
      setError(ENV_LIMIT_REACHED_MESSAGE);
      return;
    }
    // Prevent duplicate environment names within the same project (case-insensitive)
    const normalizedNewName = normalizeEnvName(sanitizedName);
    const hasDuplicateName = environments.some(
      (env) => normalizeEnvName(env.name) === normalizedNewName
    );
    if (hasDuplicateName) {
      setError('Environment name already exists');
      return;
    }
    const rawUrl = newEnvironment.url.trim();
    if (hasDuplicateEnvironmentUrl(rawUrl, environments)) {
      setError('Environment URL already exists in this project');
      return;
    }
    const urlError = validateEnvironmentUrl(newEnvironment.url);
    if (urlError) {
      setError(urlError);
      return;
    }
    setLoading(true);
    setError(null);
    const sourceIdForDuplicate = duplicateSourceEnvironmentId;
    try {
      const created = await apiService.createEnvironment(selectedProject.id, {
        ...newEnvironment,
        name: sanitizedName,
        url: rawUrl,
        baseUrl: rawUrl,
      });
      let createdResolved = created;
      if (sourceIdForDuplicate != null) {
        try {
          const vars = await apiService.getEnvironmentVariables(sourceIdForDuplicate);
          const sourceVars: EnvironmentVariable[] = Array.isArray(vars)
            ? vars
            : Array.isArray((vars as { content?: EnvironmentVariable[] }).content)
            ? ((vars as { content: EnvironmentVariable[] }).content)
            : Array.isArray((vars as { data?: EnvironmentVariable[] }).data)
            ? ((vars as { data: EnvironmentVariable[] }).data)
            : [];
          const envScoped = sourceVars.filter((v) => (v.scope || '').toLowerCase() === 'environment');
          const seenKeys = new Set<string>();
          const uniqueForReplace = envScoped.filter((v) => {
            const k = (v.key || '').trim();
            if (!k || seenKeys.has(k)) return false;
            seenKeys.add(k);
            return true;
          });
          if (uniqueForReplace.length > 0) {
            await apiService.replaceEnvironmentVariables(
              createdResolved.id,
              uniqueForReplace.map((v) => ({
                key: v.key,
                value: v.value,
                description: v.description,
                extractFromResponse: v.extractFromResponse,
                extractJsonPath: v.extractJsonPath,
                extractHeaderName: v.extractHeaderName,
              }))
            );
          }
        } catch (copyErr) {
          console.error('Environment created, but copying variables failed:', copyErr);
        }
      }
      setEnvironments((prev) => [createdResolved, ...prev]);
      setCreateDialogOpen(false);
      setDuplicateSourceEnvironmentId(null);
      setNewEnvironment({ name: '', description: '', url: '' });
      setSelectedEnvironment(createdResolved);
      void refreshSubscriptionLimits();
    } catch (err) {
      const status = (err as any)?.response?.status;
      const errorCode = (err as any)?.response?.data?.errorCode;
      const backendMessage =
        (err as any)?.response?.data?.message ||
        (err as any)?.response?.data?.error ||
        (err as any)?.message;
      if (
        errorCode === 'PLAN_LIMIT_EXCEEDED' ||
        (typeof backendMessage === 'string' &&
          (backendMessage.toLowerCase().includes('plan limit') ||
            backendMessage.toLowerCase().includes('environment limit')))
      ) {
        setError(ENV_LIMIT_REACHED_MESSAGE);
      } else if (typeof backendMessage === 'string' && backendMessage.trim()) {
        setError(backendMessage);
      } else if (status === 400) {
        setError('Invalid environment data');
      } else {
        setError('Failed to create environment');
      }
      console.error('Error creating environment:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setDuplicateSourceEnvironmentId(null);
    setNewEnvironment({ name: '', description: '', url: '' });
    setError(null);
  };

  const handleEditEnvironment = async () => {
    const sanitizedName = normalizeEnvironmentNameForSave(editForm.name);
    if (!editingEnvironment || !sanitizedName) {
      setError('Environment name is required');
      return;
    }
    if (sanitizedName.length > ENVIRONMENT_NAME_MAX_LENGTH) {
      setError(`Environment name must be ${ENVIRONMENT_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }
    // Prevent duplicate environment names within the same project (case-insensitive)
    const normalizedNewName = normalizeEnvName(sanitizedName);
    const normalizedCurrentName = normalizeEnvName(editingEnvironment.name);
    if (normalizedNewName !== normalizedCurrentName) {
      const hasDuplicateName = environments.some(
        (env) =>
          env.id !== editingEnvironment.id &&
          normalizeEnvName(env.name) === normalizedNewName
      );
      if (hasDuplicateName) {
        setError('Environment name already exists');
        return;
      }
    }
    const rawUrl = nameOnlyEdit ? (editingEnvironment.baseUrl ?? editingEnvironment.url ?? '') : editForm.url.trim();
    if (!nameOnlyEdit) {
      const editUrlError = validateEnvironmentUrl(editForm.url);
      if (editUrlError) {
        setError(editUrlError);
        return;
      }
      if (hasDuplicateEnvironmentUrl(rawUrl, environments, editingEnvironment.id)) {
        setError('Environment URL already exists in this project');
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      const updated = await apiService.updateEnvironment(editingEnvironment.id, {
        ...editForm,
        name: sanitizedName,
        description: nameOnlyEdit ? (editingEnvironment.description ?? '') : editForm.description,
        url: rawUrl,
        baseUrl: rawUrl,
      });
      setEnvironments((prev) =>
        prev.map((e) => (e.id === editingEnvironment.id ? updated : e))
      );
      if (selectedEnvironment?.id === editingEnvironment.id) {
        setSelectedEnvironment(updated);
        setBaseUrl(updated.baseUrl ?? updated.url ?? '');
      }
      setEditDialogOpen(false);
      setEditingEnvironment(null);
      setNameOnlyEdit(false);
      setEditForm({ name: '', description: '', url: '' });
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to update environment'));
      console.error('Error updating environment:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setEnvironmentToDelete(null);
    setDeleteDialogBlockedMessage(null);
  };

  const openDeleteDialog = async (env: Environment) => {
    setContextMenuAnchor(null);
    setContextMenuEnvironment(null);
    setDeleteDialogBlockedMessage(null);
    setEnvironmentToDelete(env);
    setDeleteDialogOpen(true);

    if ((env.requestCount ?? 0) > 0) {
      return;
    }

    const isLastActiveEnvironment = environments.length === 1;
    if (!isLastActiveEnvironment || !selectedProject?.id) {
      return;
    }

    try {
      const projectVars = await apiService.getVariablesByProject(selectedProject.id);
      const projectScopedCount = countManageableProjectScopedVariables(projectVars);
      if (projectScopedCount > 0) {
        setDeleteDialogBlockedMessage(
          buildLastEnvironmentProjectVarsBlockMessage(env.name, projectScopedCount)
        );
      }
    } catch (err) {
      console.error('Failed to check project-scoped variables before delete:', err);
    }
  };

  const handleDeleteEnvironment = async () => {
    if (!environmentToDelete) return;
    if ((environmentToDelete.requestCount ?? 0) > 0) {
      return;
    }
    setLoading(true);
    setError(null);
    setDeleteDialogBlockedMessage(null);
    try {
      await apiService.deleteEnvironment(environmentToDelete.id);
      setEnvironments((prev) => {
        const next = prev.filter((e) => e.id !== environmentToDelete.id);
        // If we just deleted the currently selected environment, move selection to the next one (if any)
        if (selectedEnvironment?.id === environmentToDelete.id) {
          setSelectedEnvironment(next.length > 0 ? next[0] : null);
        }
        return next;
      });
      closeDeleteDialog();
      void refreshSubscriptionLimits();
    } catch (err) {
      const message = extractApiErrorMessage(err, 'Failed to delete environment');
      if (message.toLowerCase().includes('cannot delete environment')) {
        setDeleteDialogBlockedMessage(message);
      } else {
        setError(message);
        closeDeleteDialog();
      }
      console.error('Error deleting environment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateEnvironment = () => {
    if (!contextMenuEnvironment || !selectedProject?.id) return;
    if (isEnvironmentLimitReached) {
      setError(ENV_LIMIT_REACHED_MESSAGE);
      setContextMenuAnchor(null);
      setContextMenuEnvironment(null);
      return;
    }
    const duplicateName = getUniqueDuplicateEnvironmentName(
      contextMenuEnvironment.name,
      environments
    );
    setDuplicateSourceEnvironmentId(contextMenuEnvironment.id);
    setNewEnvironment({
      name: duplicateName,
      description: contextMenuEnvironment.description || '',
      url: '',
    });
    setError(null);
    setCreateDialogOpen(true);
    setContextMenuAnchor(null);
    setContextMenuEnvironment(null);
  };

  const openEditDialog = (env: Environment, mode: 'full' | 'name-only' = 'full') => {
    setContextMenuAnchor(null);
    setContextMenuEnvironment(null);
    setEditingEnvironment(env);
    setNameOnlyEdit(mode === 'name-only');
    setEditForm({
      name: sanitizeEnvironmentNameWhileTyping(env.name || ''),
      description: env.description || '',
      url: env.baseUrl ?? env.url ?? '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveBaseUrl = async () => {
    if (!selectedEnvironment) return;
    const rawUrl = baseUrl.trim();
    const urlError = validateEnvironmentUrl(rawUrl);
    if (urlError) {
      setError('Invalid base url');
      return;
    }
    if (hasDuplicateEnvironmentUrl(rawUrl, environments, selectedEnvironment.id)) {
      setError('Environment URL already exists in this project');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const updated = await apiService.updateEnvironment(selectedEnvironment.id, {
        name: selectedEnvironment.name,
        description: selectedEnvironment.description,
        url: rawUrl,
        baseUrl: rawUrl,
      });
      setEnvironments((prev) =>
        prev.map((e) => (e.id === selectedEnvironment.id ? updated : e))
      );
      setSelectedEnvironment(updated);
      setSuccess('Base URL saved.');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      const status = (err as { response?: { status?: number; data?: { message?: string; error?: string } } })
        ?.response?.status;
      const backendMessage =
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message ||
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.error ||
        (err as { message?: string })?.message;
      if (status === 400) {
        setError(
          typeof backendMessage === 'string' && backendMessage.trim()
            ? backendMessage
            : 'Invalid base url'
        );
      } else if (
        typeof backendMessage === 'string' &&
        /invalid|url|base url/i.test(backendMessage)
      ) {
        setError(backendMessage.trim() || 'Invalid base url');
      } else {
        setError('Failed to save base URL');
      }
      console.error('Error saving base URL:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={environmentsPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.25,
            pb: 0.5,
            minHeight: 36,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              height: 36,
              lineHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '-0.02em',
              color: DFT_BRAND.text,
            }}
          >
            Environments
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 36,
              '& .MuiButton-root': {
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              },
              '& .MuiButton-startIcon': {
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: 0,
                marginBottom: 0,
              },
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<HelpOutlineIcon />}
              onClick={() => navigate(getHelpPathForPage('ENVIRONMENTS'))}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#1d4ed8',
                borderColor: '#bfdbfe',
                bgcolor: '#ffffff',
                height: 36,
                minHeight: 36,
                borderRadius: 0,
                px: 1.1,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#93c5fd',
                  bgcolor: '#eff6ff',
                },
              }}
              aria-label="Open help for Environments"
            >
              Help
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                if (isEnvironmentLimitReached) {
                  setError(ENV_LIMIT_REACHED_MESSAGE);
                  return;
                }
                setCreateDialogOpen(true);
              }}
              disabled={!selectedProject?.id || isEnvironmentLimitReached}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                height: 36,
                minHeight: 36,
                borderRadius: 1.5,
                bgcolor: '#1d4ed8',
                '&:hover': { bgcolor: '#1e40af' },
                px: 1.2,
                fontSize: '0.875rem',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Create Environment
            </Button>
            <FormControl
              ref={projectSelectRef}
              size="small"
              variant="outlined"
              sx={{
                width: { xs: 180, sm: 220, md: 240, lg: PROJECT_SELECT_CONTROL_WIDTH.sm },
                flexShrink: 0,
                alignSelf: 'center',
                overflow: 'visible',
                '& .MuiInputLabel-root': {
                  backgroundColor: DFT_BRAND.paper,
                  px: 0.5,
                  lineHeight: 1.25,
                  zIndex: 1,
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiOutlinedInput-root': {
                  height: 36,
                  minHeight: 36,
                  backgroundColor: DFT_BRAND.paper,
                  borderRadius: '10px',
                },
                '& .MuiOutlinedInput-notchedOutline legend': {
                  maxWidth: '100%',
                },
                '& .MuiOutlinedInput-notchedOutline legend span': {
                  paddingLeft: '4px',
                  paddingRight: '4px',
                },
              }}
            >
            <InputLabel id="env-project-select-label" shrink>
              Project
            </InputLabel>
            <Select
              labelId="env-project-select-label"
              id="env-project-select"
              value={selectedProject ? selectedProject.id : ''}
              label="Project"
              open={projectZoomMenu.menuOpen}
              onOpen={(e) => {
                const width = projectSelectRef.current?.getBoundingClientRect().width ?? 0;
                if (width > 0) setProjectMenuWidth(Math.round(width));
                projectZoomMenu.onOpen(e);
              }}
              onClose={projectZoomMenu.onClose}
              onChange={(e) => {
                const projectId = Number(e.target.value);
                const project = projects.find((p) => Number(p.id) === projectId);
                setSelectedProject(project || null);
              }}
              sx={{
                width: '100%',
                bgcolor: DFT_BRAND.paper,
                '& .MuiSelect-select': {
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  lineHeight: 1.35,
                  display: 'block',
                  boxSizing: 'border-box',
                  whiteSpace: 'nowrap',
                },
              }}
              MenuProps={projectZoomMenu.MenuProps}
            >
              {projectsLoading ? (
                <MenuItem disabled value="">
                  <CircularProgress size={20} /> Loading...
                </MenuItem>
              ) : (
                projects.map((project) => (
                  <MenuItem key={project.id} value={project.id} title={project.name}>
                    {project.name}
                  </MenuItem>
                ))
              )}
              {projects.length === 0 && !projectsLoading && (
                <MenuItem disabled>No projects found</MenuItem>
              )}
            </Select>
          </FormControl>
          </Box>
        </Box>
      </Box>

      <Box sx={ENVIRONMENTS_WORKSPACE_SX}>
      {/* Left panel: environment list */}
      <Paper
        elevation={0}
        sx={{
          width: LEFT_PANEL_WIDTH,
          minWidth: { md: 220, lg: 248, xl: 280 },
          maxWidth: LEFT_PANEL_WIDTH,
          height: { xs: 'auto', md: '100%' },
          maxHeight: { xs: 280, sm: 320, md: 'none' },
          minHeight: 0,
          flex: { xs: '0 0 auto', md: '0 0 auto' },
          borderRight: { md: `1px solid ${DFT_BRAND.line}` },
          borderBottom: { xs: `1px solid ${DFT_BRAND.line}`, md: 'none' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: DFT_BRAND.paper,
          boxShadow: 'none',
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 1, sm: 1.25 },
            pt: 1,
            pb: 1,
            minHeight: 36,
            borderBottom: `1px solid ${DFT_BRAND.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.875rem',
              color: DFT_BRAND.text,
              letterSpacing: '-0.01em',
              height: 36,
              lineHeight: '36px',
              display: 'block',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Environments ({environmentCount}/{maxEnvironments})
          </Typography>
          <IconButton
            color="primary"
            onClick={() => {
              if (isEnvironmentLimitReached) {
                setError(ENV_LIMIT_REACHED_MESSAGE);
                return;
              }
              setCreateDialogOpen(true);
            }}
            title="Create environment"
            disabled={!selectedProject?.id || isEnvironmentLimitReached}
          >
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <List sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto' }}>
          {!selectedProject?.id ? (
            <Box sx={{ px: 2, py: 2 }}><Typography color="text.secondary">Select a project to view and manage environments.</Typography></Box>
          ) : loading && environments.length === 0 ? (
            <Box sx={{ px: 2, py: 2 }}><Typography color="text.secondary">Loading...</Typography></Box>
          ) : environments.length === 0 ? (
            <Box sx={{ px: 2, py: 2 }}><Typography color="text.secondary">No environments. Create one to get started.</Typography></Box>
          ) : (
            paginatedEnvironments.map((env) => (
              <ListItemButton
                key={env.id}
                selected={selectedEnvironment?.id === env.id}
                onClick={() => handleSelectEnvironment(env)}
                sx={{
                  py: 0.5,
                  px: 0.5,
                  mx: 0.5,
                  mb: 0.125,
                  borderRadius: '8px',
                  minHeight: 40,
                  '&.Mui-selected': {
                    bgcolor: DFT_BRAND.tint,
                    '&:hover': { bgcolor: DFT_BRAND.tint },
                  },
                  '&:hover': { bgcolor: DFT_BRAND.tint },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <EnvironmentIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={env.name}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                  secondary={environmentListSecondary(env)}
                  secondaryTypographyProps={{ noWrap: true, variant: 'caption', fontSize: '0.75rem' }}
                />
                <IconButton
                  size="small"
                  edge="end"
                  sx={{ width: 28, height: 28, p: 0.25 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setContextMenuEnvironment(env);
                    setContextMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))
          )}
        </List>
        {selectedProject?.id && environments.length > 0 && (
          <EnvironmentsSidebarPagination
            count={environments.length}
            page={effectiveSidebarPage}
            rowsPerPage={sidebarRowsPerPage}
            onPageChange={setSidebarPage}
            onRowsPerPageChange={(rowsPerPage) => {
              setSidebarRowsPerPage(rowsPerPage);
              setSidebarPage(0);
            }}
          />
        )}
        </Box>
      </Paper>

      {/* Right panel */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, bgcolor: DFT_BRAND.bg, overflow: 'auto' }}>
        {error && !createDialogOpen && !editDialogOpen && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ m: 2 }}>
            {success}
          </Alert>
        )}

        {!selectedEnvironment ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              p: 4,
            }}
          >
            <Typography color="text.secondary">
              {environments.length === 0
                ? 'Create an environment to get started'
                : 'Select an environment from the list'}
            </Typography>
            {environments.length > 0 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (isEnvironmentLimitReached) {
                    setError(ENV_LIMIT_REACHED_MESSAGE);
                    return;
                  }
                  setCreateDialogOpen(true);
                }}
                disabled={isEnvironmentLimitReached}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 36,
                  minHeight: 36,
                  borderRadius: 1.5,
                  color: '#1d4ed8',
                  borderColor: '#bfdbfe',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    borderColor: '#93c5fd',
                    bgcolor: '#eff6ff',
                  },
                }}
              >
                Create Environment
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ flex: 1, p: { xs: 1.25, sm: 1.5 }, width: '100%', minWidth: 0, alignSelf: 'stretch' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, minWidth: 0 }}>
              <Typography
                variant="h6"
                title={selectedEnvironment.name}
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  color: DFT_BRAND.text,
                  letterSpacing: '-0.02em',
                }}
              >
                {selectedEnvironment.name}
              </Typography>
              <IconButton
                size="small"
                aria-label="Edit environment name"
                onClick={() => openEditDialog(selectedEnvironment, 'name-only')}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Used in {formatEnvironmentRequestUsage(selectedEnvironment.requestCount)}
            </Typography>

            <Card elevation={0} sx={environmentsCardSx}>
              <CardContent sx={{ p: '12px !important' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Base URL
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: { xs: 'stretch', sm: 'flex-start' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={baseUrl}
                    onChange={(e) => {
                      const next = e.target.value;
                      setBaseUrl(next);
                      // Clear stale banner once the current Base URL is valid (or empty).
                      if (!next.trim() || !validateEnvironmentUrl(next)) {
                        setError((prev) =>
                          prev &&
                          (prev === 'Invalid base url' ||
                            prev === 'Failed to save base URL' ||
                            /url|hostname|port|protocol/i.test(prev))
                            ? null
                            : prev
                        );
                      }
                    }}
                    placeholder="https://api.example.com"
                    error={!!baseUrl.trim() && !!validateEnvironmentUrl(baseUrl)}
                    helperText={
                      baseUrl.trim() ? validateEnvironmentUrl(baseUrl) || undefined : undefined
                    }
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveBaseUrl}
                    disabled={loading || !!validateEnvironmentUrl(baseUrl)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      height: 36,
                      minHeight: 36,
                      borderRadius: 1.5,
                      bgcolor: '#1d4ed8',
                      '&:hover': { bgcolor: '#1e40af' },
                      boxShadow: 'none',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      fontSize: '0.875rem',
                      alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Environment variables: inline key / value / description (no modal) */}
            <Card elevation={0} sx={{ ...environmentsCardSx, mb: 0 }}>
              <CardContent sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', p: '12px !important' }}>
                <EnvironmentVariablesPage
                  environmentId={selectedEnvironment.id}
                  projectId={selectedProject?.id}
                />
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
      </Box>

      {/* Context menu: keep default portal (body). Popover uses viewport coords; inside the tenant layout's
          CSS zoom subtree, disablePortal misaligns the paper vs the ⋮ anchor. Open to the right of the
          ⋮ so the menu stays in the content area and does not overlap the app sidebar. */}
      <Menu
        open={Boolean(contextMenuAnchor)}
        anchorEl={contextMenuAnchor}
        onClose={() => { setContextMenuAnchor(null); setContextMenuEnvironment(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: '220px !important',
            width: 'max-content !important',
            maxWidth: 'min(360px, calc(100vw - 16px)) !important',
            maxHeight: '80vh',
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <MenuItem onClick={() => contextMenuEnvironment && openEditDialog(contextMenuEnvironment)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicateEnvironment} disabled={isEnvironmentLimitReached}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
          Duplicate
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => contextMenuEnvironment && openDeleteDialog(contextMenuEnvironment)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Create dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            m: 2,
            borderRadius: '14px',
            border: `1px solid ${DFT_BRAND.line}`,
          },
        }}
      >
        <DialogTitle>{duplicateSourceEnvironmentId != null ? 'Duplicate Environment' : 'Create New Environment'}</DialogTitle>
        <DialogContent sx={{ pt: 2, ...environmentPlaceholderSx }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Environment Name"
            fullWidth
            variant="outlined"
            value={newEnvironment.name}
            onChange={(e) =>
              setNewEnvironment((prev) => ({
                ...prev,
                name: sanitizeEnvironmentNameWhileTyping(e.target.value),
              }))
            }
            inputProps={{ maxLength: ENVIRONMENT_NAME_MAX_LENGTH }}
            helperText={`${newEnvironment.name.length}/${ENVIRONMENT_NAME_MAX_LENGTH}`}
            sx={{ mb: 1.5 }}
            size="small"
          />
          <TextField
            margin="dense"
            label="Base URL"
            fullWidth
            variant="outlined"
            value={newEnvironment.url}
            onChange={(e) => setNewEnvironment((prev) => ({ ...prev, url: e.target.value }))}
            placeholder="https://api.example.com"
            error={!!newEnvironment.url.trim() && !!validateEnvironmentUrl(newEnvironment.url)}
            helperText={
              newEnvironment.url.trim()
                ? validateEnvironmentUrl(newEnvironment.url) ||
                  (duplicateSourceEnvironmentId != null
                    ? 'Enter a unique base URL for the duplicated environment'
                    : 'Enter the base URL for this environment')
                : duplicateSourceEnvironmentId != null
                  ? 'Enter a unique base URL for the duplicated environment'
                  : 'Enter the base URL for this environment'
            }
            sx={{ mb: 1 }}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            onClick={handleCreateEnvironment}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={
              loading ||
              isEnvironmentLimitReached ||
              !selectedProject?.id ||
              !newEnvironment.name.trim() ||
              !newEnvironment.url.trim() ||
              !!validateEnvironmentUrl(newEnvironment.url)
            }
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setNameOnlyEdit(false);
          setError(null);
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            m: 2,
            borderRadius: '14px',
            border: `1px solid ${DFT_BRAND.line}`,
          },
        }}
      >
        <DialogTitle>{nameOnlyEdit ? 'Edit Environment Name' : 'Edit Environment'}</DialogTitle>
        <DialogContent sx={{ pt: 2, ...environmentPlaceholderSx }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Environment Name"
            fullWidth
            variant="outlined"
            value={editForm.name}
            onChange={(e) =>
              setEditForm((prev) => ({
                ...prev,
                name: sanitizeEnvironmentNameWhileTyping(e.target.value),
              }))
            }
            inputProps={{ maxLength: ENVIRONMENT_NAME_MAX_LENGTH }}
            helperText={`${editForm.name.length}/${ENVIRONMENT_NAME_MAX_LENGTH}`}
            sx={{ mb: 1.5 }}
            size="small"
          />
          {!nameOnlyEdit && (
              <TextField
                margin="dense"
                label="Base URL"
                fullWidth
                variant="outlined"
                value={editForm.url}
                onChange={(e) => setEditForm((prev) => ({ ...prev, url: e.target.value }))}
                placeholder="https://api.example.com"
                error={!!editForm.url.trim() && !!validateEnvironmentUrl(editForm.url)}
                helperText={
                  editForm.url.trim()
                    ? validateEnvironmentUrl(editForm.url) ||
                      'Enter the base URL for this environment'
                    : 'Enter the base URL for this environment'
                }
                sx={{ mb: 1 }}
                size="small"
              />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setNameOnlyEdit(false);
              setError(null);
            }}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditEnvironment}
            variant="contained"
            startIcon={<SaveIcon />}
            // Allow any non-empty URL so the user can click Update;
            // the handler will validate and show an error if needed.
            disabled={
              loading ||
              !editForm.name.trim() ||
              (!nameOnlyEdit && !editForm.url.trim()) ||
              (!nameOnlyEdit && !!validateEnvironmentUrl(editForm.url))
            }
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            m: 2,
            borderRadius: '14px',
            border: `1px solid ${DFT_BRAND.line}`,
          },
        }}
      >
        {(() => {
          const blockedByUsage = (environmentToDelete?.requestCount ?? 0) > 0;
          const blockedMessage =
            deleteDialogBlockedMessage ??
            (blockedByUsage && environmentToDelete
              ? `Cannot delete "${environmentToDelete.name}" because this environment is used in ${formatEnvironmentRequestUsage(environmentToDelete.requestCount)}.`
              : null);
          const blockedByProjectVars =
            !!blockedMessage && blockedMessage.toLowerCase().includes('project-scoped');
          if (blockedMessage) {
            return (
              <>
                <DialogTitle>Cannot Delete Environment</DialogTitle>
                <DialogContent>
                  <DialogContentText>{blockedMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                  {blockedByProjectVars && environmentToDelete ? (
                    <Button
                      onClick={() => {
                        setSelectedEnvironment(environmentToDelete);
                        closeDeleteDialog();
                      }}
                      variant="outlined"
                    >
                      Manage variables
                    </Button>
                  ) : null}
                  <Button onClick={closeDeleteDialog} variant="contained">
                    OK
                  </Button>
                </DialogActions>
              </>
            );
          }
          return (
            <>
              <DialogTitle>Delete Environment</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete &quot;{environmentToDelete?.name}&quot;? This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDeleteDialog}>Cancel</Button>
                <Button onClick={handleDeleteEnvironment} color="error" variant="contained" disabled={loading}>
                  {loading ? 'Deleting...' : 'Delete'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default Environments;
